import React from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';

function TopCategoriesChart({ chartData, selectedYear, colorMap, onCategoryClick }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const chartColors = React.useMemo(() => theme.palette.chartColors || [
        theme.palette.primary.main,
        '#2563EB',
        '#7C3AED',
        '#D97706',
        '#E11D48',
        '#0891B2',
        '#C026D3',
        '#EA580C',
        '#0D9488',
        '#64748B'
    ], [theme]);

    const processedChartData = React.useMemo(() => {
        if (!chartData || !Array.isArray(chartData)) return [];
        let nonOtherIndex = 0;
        return chartData.map((item) => {
            let color = item.color;
            if (!color && colorMap && colorMap[item.label]) {
                color = colorMap[item.label];
            }
            if (!color) {
                if (item.label === 'Other') {
                    color = '#64748B';
                } else {
                    color = chartColors[nonOtherIndex % chartColors.length];
                    nonOtherIndex++;
                }
            }
            return {
                ...item,
                rawName: item.rawName || item.label,
                label: t(item.label),
                color
            };
        });
    }, [chartData, colorMap, chartColors, t]);

    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                height: '100%',
                p: { xs: 2, sm: 4 },
                bgcolor: 'background.paper',
                minHeight: { xs: 'auto', sm: 350 },
                boxShadow: '0 12px 32px rgba(28, 28, 24, 0.04)'
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 700,
                    mb: { xs: 2, sm: 4 },
                    color: 'text.primary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.9rem'
                }}
            >
                {t('Spending by Category')}
            </Typography>

            {processedChartData.length > 0 ? (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    overflow: 'visible'
                }}>
                    <PieChart
                        margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                        series={[
                            {
                                data: processedChartData,
                                innerRadius: 75,
                                outerRadius: isMobile ? 95 : 105,
                                paddingAngle: 2,
                                cornerRadius: 4,
                                highlightScope: { faded: 'global', highlighted: 'item' },
                                arcLabel: (item) => isMobile ? '' : `$${(item.value / 1000).toFixed(1)}k`,
                                arcLabelMinAngle: 30,
                                arcLabelRadius: 132,
                            },
                        ]}
                        onItemClick={(event, d) => {
                            if (onCategoryClick && d && typeof d.dataIndex === 'number') {
                                const item = processedChartData[d.dataIndex];
                                if (item) {
                                    const topCategories = processedChartData.filter(c => c.rawName !== 'Other').map(c => c.rawName);
                                    onCategoryClick({
                                        categoryName: item.rawName,
                                        amount: item.value,
                                        isAnnual: true,
                                        topCategories
                                    });
                                }
                            }
                        }}
                        sx={{
                            overflow: 'visible',
                            '& .MuiChartsSurface-root': {
                                overflow: 'visible'
                            },
                            '& .MuiPieArc-root': {
                                cursor: onCategoryClick ? 'pointer' : 'default'
                            },
                            [`& .${pieArcLabelClasses.root}`]: {
                                fill: 'text.primary',
                                fontWeight: 700,
                                fontSize: 12,
                                fontFamily: '"Manrope", sans-serif'
                            },
                        }}
                        width={isMobile ? 320 : 440}
                        height={isMobile ? 240 : 300}
                        slotProps={{
                            legend: {
                                direction: 'row',
                                position: {
                                    vertical: 'top',
                                    horizontal: 'center'
                                },
                                itemMarkWidth: 8,
                                itemMarkHeight: 8,
                                markGap: 6,
                                itemGap: isMobile ? 8 : 12,
                                labelStyle: {
                                    fontSize: 12,
                                    fontWeight: 600,
                                    fill: theme.palette.text.secondary,
                                    fontFamily: '"Work Sans", sans-serif'
                                }
                            }
                        }}
                        colors={chartColors}
                    />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <Typography color="text.secondary">{t('No data available')}</Typography>
                </Box>
            )}
        </Paper>
    );
}

export default React.memo(TopCategoriesChart);
